import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "lineItemsContainer",
    "lineItemTemplate",
    "lineItem",
    "destroy",
    "productSelect",
    "quantity",
    "unitCost",
    "lineTotal",
    "discountFieldWrapper",
    "discountContainer",
    "discountPrefix",
    "discountAmount",
    "discountType",
    "syncStatus",
    "validationErrors",
    "errorList"
  ]

  connect() {
    // Initialize errors array for frontend validation
    this.errors = []

    this.updateLineItemNumbers()
    this.calculateAllLineTotals()

    // Store original discount values for restoration
    if (this.hasDiscountTypeTarget && this.hasDiscountAmountTarget) {
      this.originalDiscountType = this.discountTypeTarget.value
      this.originalDiscountAmount = this.discountAmountTarget.value
    }

    this.updateDiscountDisplay()
    this.updateOrderSummary()

    // Listen for typeahead selection events
    this.element.addEventListener('typeahead:selected', this.handleProductSelected.bind(this))

    // Use event delegation for refresh buttons (since they're added dynamically)
    this.element.addEventListener('click', (e) => {
      if (e.target.closest('button[data-product-sku]')) {
        e.preventDefault()
        this.refreshProductSyncStatus(e)
      }
    })
  }

  // erik/TODO: Probably want to clean this up, but I'm really not too worried
  // about it given that it's in the admin section and not a user facing issue
  // that's likely to require a lot of maintenance or change in the future.
  // Can come back to this if we really need to later on.
  // Had a bunch of issues with this and the numbering of the elements, need to
  // mess with the DOM more than I would like to here to get this to work.
  addLineItem() {
    // Grab the hidden template row's HTML
    let content = this.lineItemTemplateTarget.innerHTML

    // Generate a unique timestamp-based index to replace NEW_RECORD
    const newId = Date.now()

    // - Replace the placeholder index
    // - Strip any disabled attributes so the new inputs are enabled & validated
    let newContent = content
      .replace(/NEW_RECORD/g, newId)
      .replace(/ disabled="disabled"/g, '')
      .replace(/ disabled=""/g, '')

    // Insert the enabled clone into the DOM
    this.lineItemsContainerTarget.insertAdjacentHTML('beforeend', newContent)

    this.updateLineItemNumbers()
    this.calculateAllLineTotals()
  }

  removeLineItem(event) {
    const lineItem = event.target.closest('[data-purchase-order-form-target="lineItem"]')
    const destroyField = lineItem.querySelector('[data-purchase-order-form-target="destroy"]')

    // erik/TODO: Probably only relevant for edit, and I think we're going to want
    // to be a LOT more specific about how this gets handled with the final version
    // of this. Moving past it for now and will make a note of that.
    if (destroyField && destroyField.value !== "true") {
      // If this is a persisted record, mark for destruction
      if (lineItem.querySelector('input[name*="[id]"]')) {
        destroyField.value = "true"
        lineItem.style.display = "none"
      } else {
        // If this is a new record, remove it completely
        lineItem.remove()
      }
    }

    this.updateLineItemNumbers()
    this.calculateAllLineTotals()
  }

  calculateLineTotal(event) {
    const lineItem = event.target.closest('[data-purchase-order-form-target="lineItem"]')
    if (!lineItem) return

    const quantityInput = lineItem.querySelector('[data-purchase-order-form-target="quantity"]')
    const unitCostInput = lineItem.querySelector('[data-purchase-order-form-target="unitCost"]')
    const lineTotalTarget = lineItem.querySelector('[data-purchase-order-form-target="lineTotal"]')

    if (!quantityInput || !unitCostInput || !lineTotalTarget) return

    const quantity = parseFloat(quantityInput.value) || 0
    const unitCost = parseFloat(unitCostInput.value) || 0
    const total = quantity * unitCost

    lineTotalTarget.textContent = this.formatCurrency(total)
    this.updateOrderSummary()
  }

  calculateAllLineTotals() {
    this.lineItemTargets.forEach(lineItem => {
      if (lineItem.style.display !== "none") {
        const quantityInput = lineItem.querySelector('[data-purchase-order-form-target="quantity"]')
        const unitCostInput = lineItem.querySelector('[data-purchase-order-form-target="unitCost"]')
        const lineTotalTarget = lineItem.querySelector('[data-purchase-order-form-target="lineTotal"]')

        if (quantityInput && unitCostInput && lineTotalTarget) {
          const quantity = parseFloat(quantityInput.value) || 0
          const unitCost = parseFloat(unitCostInput.value) || 0
          const total = quantity * unitCost
          lineTotalTarget.textContent = this.formatCurrency(total)
        }
      }
    })
    this.updateOrderSummary()
  }

  updateOrderSummary() {
    const subtotal = this.calculateSubtotal()
    const costs = this.getOrderCosts()
    const discountInDollars = this.calculateDiscountInDollars(subtotal, costs.discountAmount, costs.discountType)
    const grandTotal = (subtotal + (costs.shippingCost || 0) + (costs.additionalCosts || 0) - discountInDollars) || 0

    this.updateSummaryDisplay({
      subtotal,
      shippingCost: costs.shippingCost,
      additionalCosts: costs.additionalCosts,
      discountAmount: costs.discountAmount,
      discountType: costs.discountType,
      discountInDollars,
      grandTotal
    })
  }

  calculateSubtotal() {
    let subtotal = 0
    this.lineItemTargets.forEach(lineItem => {
      if (lineItem.style.display !== "none") {
        const quantityInput = lineItem.querySelector('[data-purchase-order-form-target="quantity"]')
        const unitCostInput = lineItem.querySelector('[data-purchase-order-form-target="unitCost"]')

        if (quantityInput && unitCostInput) {
          const quantity = parseFloat(quantityInput.value) || 0
          const unitCost = parseFloat(unitCostInput.value) || 0
          subtotal += quantity * unitCost
        }
      }
    })
    return subtotal
  }

  getOrderCosts() {
    return {
      shippingCost: this.getFieldValue('shipping_cost'),
      additionalCosts: this.getFieldValue('additional_costs'),
      discountAmount: this.getFieldValue('discount_amount'),
      discountType: this.getFieldValue('discount_type')
    }
  }

  calculateDiscountInDollars(subtotal, discountAmount, discountType) {
    // Calculate discount in dollars - percentage only applies to subtotal, not shipping/additional costs
    // erik/TODO: Need to verify exactly what quantities the discount needs to apply to
    if (discountAmount <= 0) return 0

    if (discountType === 'percentage') {
      // Percentage discount only applies to the subtotal
      return subtotal * (discountAmount / 100.0 || 0);
    } else {
      // Fixed amount discount
      return discountAmount
    }
  }

  updateSummaryDisplay(values) {
    // Update main summary values
    this.updateElementText('[data-po-summary-target="subtotal"]', this.formatCurrency(values.subtotal))
    this.updateElementText('[data-po-summary-target="shipping"]', this.formatCurrency(values.shippingCost))
    this.updateElementText('[data-po-summary-target="additionalCosts"]', this.formatCurrency(values.additionalCosts))
    this.updateElementText('[data-po-summary-target="grandTotal"]', this.formatCurrency(values.grandTotal))

    // Update discount display
    this.updateDiscountRow(values.discountInDollars, values.discountAmount, values.discountType)
  }

  updateElementText(selector, text) {
    const element = document.querySelector(selector)
    if (element) element.textContent = text
  }

  updateDiscountRow(discountInDollars, discountAmount, discountType) {
    const discountRow = document.querySelector('[data-po-summary-target="discountRow"]')
    const discountLabel = document.querySelector('[data-po-summary-target="discountLabel"]')
    const discountValue = document.querySelector('[data-po-summary-target="discount"]')

    if (discountInDollars > 0) {
      if (discountRow) discountRow.style.display = 'table-row'
      if (discountValue) discountValue.textContent = `-${this.formatCurrency(discountInDollars)}`

      if (discountLabel) {
        if (discountType === 'percentage') {
          discountLabel.textContent = `Discount (${discountAmount}%)`
        } else {
          discountLabel.textContent = 'Discount'
        }
      }
    } else {
      if (discountRow) discountRow.style.display = 'none'
    }
  }

  getFieldValue(fieldName) {
    const input = this.element.querySelector(`input[name*="[${fieldName}]"], select[name*="[${fieldName}]"]`)
    if (fieldName === 'discount_type') {
      return input?.value || 'amount'
    }
    return parseFloat(input?.value) || 0
  }

    updateLineItemNumbers() {
    const allLineItems = this.lineItemsContainerTarget.querySelectorAll('[data-purchase-order-form-target="lineItem"]')

    const visibleLineItems = Array.from(allLineItems).filter(item => {
      const destroyField = item.querySelector('[data-purchase-order-form-target="destroy"]')
      const markedForDestroy = destroyField && destroyField.value === 'true'
      // offsetParent === null means element (or ancestor) has display:none
      return !markedForDestroy && item.offsetParent !== null
    })

    visibleLineItems.forEach((item, idx) => {
      const header = item.querySelector('h4')
      if (header) header.textContent = `Line Item ${idx + 1}`
    })
  }

  updateDiscountDisplay(event) {
    if (!this.hasDiscountTypeTarget || !this.hasDiscountPrefixTarget || !this.hasDiscountAmountTarget) {
      return
    }

    const discountType = this.discountTypeTarget.value
    const isPercentage = discountType === 'percentage'
    const hasDiscount = discountType !== '' && discountType !== null

    // Handle no discount case
    if (!hasDiscount) {
      this.discountAmountTarget.disabled = true
      this.discountAmountTarget.value = ''
      this.discountAmountTarget.placeholder = 'No discount'
      this.discountPrefixTarget.style.display = 'none'
      this.discountAmountTarget.classList.remove('pl-7')
      this.discountAmountTarget.classList.add('pr-3')
      this.validateDiscountAmount()
      this.updateOrderSummary()
      return
    }

    // Enable field for discount types
    this.discountAmountTarget.disabled = false

    // Handle value clearing/restoration logic on change events
    if (event && event.type === 'change') {
      if (discountType === this.originalDiscountType && this.originalDiscountAmount) {
        // Restore original value when switching back to the original type
        this.discountAmountTarget.value = this.originalDiscountAmount
      } else {
        // Clear value when switching to a different type than original
        this.discountAmountTarget.value = ''
      }
    }

    this.discountPrefixTarget.style.display = 'flex'
    this.discountAmountTarget.classList.add('pl-7')
    this.discountAmountTarget.classList.remove('pr-3')
    this.discountAmountTarget.placeholder = '0.00'
    this.discountAmountTarget.step = '0.01'
    this.discountAmountTarget.min = '0'

    if (isPercentage) {
      // Show percentage display with left-justified % sign
      this.discountAmountTarget.max = '100'

      // Update the prefix to show % instead of $
      const prefixSpan = this.discountPrefixTarget.querySelector('span')
      if (prefixSpan) {
        prefixSpan.textContent = '%'
      }
    } else {
      // Show fixed amount display
      this.discountAmountTarget.removeAttribute('max')

      // Update the prefix to show $ instead of %
      const prefixSpan = this.discountPrefixTarget.querySelector('span')
      if (prefixSpan) {
        prefixSpan.textContent = '$'
      }
    }

    this.validateDiscountAmount()
    this.updateOrderSummary()
  }

  validateDiscountAmount() {
    if (!this.hasDiscountAmountTarget || !this.hasDiscountTypeTarget) {
      return
    }

    const discountType = this.discountTypeTarget.value
    let value = this.discountAmountTarget.value
    const isPercentage = discountType === 'percentage'

    // Enforce 2 decimal places maximum
    if (value.includes('.')) {
      const parts = value.split('.')
      if (parts[1] && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2)
        this.discountAmountTarget.value = value
      }
    }

    const amount = parseFloat(value) || 0

    if (isPercentage && amount > 100) {
      this.discountAmountTarget.value = '100.00'
      this.showDiscountValidationMessage('Percentage cannot exceed 100%')
    } else if (amount < 0) {
      this.discountAmountTarget.value = '0.00'
      this.showDiscountValidationMessage('Discount cannot be negative')
    }

    // Update order summary after validation
    this.updateOrderSummary()
  }

  showDiscountValidationMessage(message) {
    // Remove any existing validation message first if it's present
    const existingMessage = this.discountFieldWrapperTarget.querySelector('.discount-validation-message')
    if (existingMessage) {
      existingMessage.remove()
    }

    const validationMessage = document.createElement('div')
    validationMessage.className = 'discount-validation-message text-sm text-red-600 mt-1'
    validationMessage.textContent = message

    // Append to the field wrapper (outside the input container)
    this.discountFieldWrapperTarget.appendChild(validationMessage)

    // Remove message after 3 seconds
    // erik/TODO: I'm not the biggest fan of this, feels a bit weird as a user
    // but let's just use this for now.
    setTimeout(() => {
      if (validationMessage && validationMessage.parentNode) {
        validationMessage.remove()
      }
    }, 3000)
  }

  warehouseChanged() {
    // erik/TODO: Filter products by warehouse
    // make AJAX calls to load inventory_product_locations for the selected warehouse
    // Upon second thought, we want to populate the "Product" dropdown field
    // to display the _entirety_ of the catalog, not just the IP.
    console.error("NOT YET IMPLEMENTED")
  }

  formatCurrency(amount) {
    // erik/TODO: currency display, should we use a library for this or is this just fine?
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount)
  }

  confirmOrder(event) {
    const confirmMessage = event.currentTarget.dataset.confirmMessage

    if (!confirm(confirmMessage)) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }
  }

  handleProductSelected(event) {
    const { product, input, hiddenField } = event.detail

    if (!product) {
      return
    }

    // Find the line item that contains this input
    const lineItem = input.closest('[data-purchase-order-form-target="lineItem"]')
    if (!lineItem) {
      return
    }

    const syncStatusTarget = lineItem.querySelector('[data-purchase-order-form-target="syncStatus"]')
    if (!syncStatusTarget) {
      return
    }

    // Display sync status with the product data we just received
    this.displaySyncStatus(product, syncStatusTarget, lineItem)
  }

  onProductSelected(event) {
    // This method is kept for backward compatibility but may not be needed
    // The typeahead:selected event should handle this now
  }

  displaySyncStatus(product, syncStatusTarget, lineItem) {
    const hasExternalId = product.external_id && product.external_id.trim() !== ''
    const productId = product.id
    const productSku = product.sku || ''

    if (hasExternalId) {
      syncStatusTarget.innerHTML = `
        <div class="flex items-center text-sm text-green-700">
          <span class="mr-2">✅</span>
          <span>Product exists in Cybership, ready to add</span>
        </div>
      `
      syncStatusTarget.className = 'sync-status mt-2'
      this.removeProductError(lineItem)
    } else {
      syncStatusTarget.innerHTML = `
        <div class="space-y-2">
          <div class="flex items-center text-sm text-yellow-700">
            <span class="mr-2">⚠️</span>
            <span>Product is not synced to Cybership. PO will not be created properly until this is addressed</span>
          </div>
          <div class="flex items-center space-x-2">
            <button type="button"
                    class="inline-flex items-center px-2 py-1 border border-yellow-300 rounded text-xs font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-yellow-500"
                    data-product-sku="${productSku}">
              <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Refresh Sync Status
            </button>
            <a href="/admin/inventory_products/${productId}/edit"
               target="_blank"
               rel="noopener noreferrer"
               class="inline-flex items-center px-2 py-1 border border-yellow-300 rounded text-xs font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-yellow-500">
              <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
              </svg>
              Edit Product
            </a>
          </div>
        </div>
      `
      syncStatusTarget.className = 'sync-status mt-2'
      this.addProductError(lineItem, product)
    }

    syncStatusTarget.style.display = 'block'
    this.updateValidationErrors()
  }

  addProductError(lineItem, product) {
    const productId = lineItem.querySelector('input[type="hidden"][name*="[inventory_product_id]"]')?.value
    const errorKey = `product_${productId}`

    // Remove existing error for this product if any
    this.removeProductError(lineItem)

    // Add new error
    this.errors.push({
      key: errorKey,
      message: `${product.name || product.sku} is not synced to Cybership. Please sync this product in the Products tab before ordering.`,
      lineItem: lineItem
    })
  }

  removeProductError(lineItem) {
    const productId = lineItem.querySelector('input[type="hidden"][name*="[inventory_product_id]"]')?.value
    const errorKey = `product_${productId}`

    this.errors = this.errors.filter(error => error.key !== errorKey)
  }


  updateValidationErrors() {
    if (this.errors.length === 0) {
      if (this.hasValidationErrorsTarget) {
        this.validationErrorsTarget.style.display = 'none'
      }
      return
    }

    // Show errors
    if (this.hasValidationErrorsTarget) {
      this.validationErrorsTarget.style.display = 'block'
    }

    // Update error list
    if (this.hasErrorListTarget) {
      this.errorListTarget.innerHTML = this.errors.map(error =>
        `<li>${error.message}</li>`
      ).join('')
    }
  }

  validateBeforeSubmit(event) {
    // Check which button was clicked - only block "order", allow "draft"
    const submitButton = event.submitter
    const isOrderButton = submitButton && submitButton.name === 'commit[order]'

    // Only validate if trying to order, not if saving as draft
    if (!isOrderButton) {
      return true
    }

    // Check if there are any errors in the array
    if (this.errors.length > 0) {
      event.preventDefault()
      event.stopPropagation()

      // Scroll to errors
      if (this.hasValidationErrorsTarget) {
        this.validationErrorsTarget.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }

      return false
    }
  }

  async refreshProductSyncStatus(event) {
    const button = event.target.closest('button[data-product-sku]')
    if (!button) return

    const productSku = button.dataset.productSku
    const lineItem = button.closest('[data-purchase-order-form-target="lineItem"]')
    const syncStatusTarget = lineItem.querySelector('[data-purchase-order-form-target="syncStatus"]')

    if (!productSku || !syncStatusTarget) {
      return
    }

    // Disable button and show loading state
    button.disabled = true
    const originalContent = syncStatusTarget.innerHTML
    syncStatusTarget.innerHTML = `
      <div class="flex items-center text-sm text-gray-600">
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Checking sync status...</span>
      </div>
    `

    try {
      // Search for the product by SKU
      const response = await fetch(`/admin/inventory_products/search?query=${encodeURIComponent(productSku)}`)
      if (response.ok) {
        const results = await response.json()
        // Get the first item from the search results
        const product = results && results.length > 0 ? results[0] : null

        if (product) {
          // Update the sync status with the fresh data
          this.displaySyncStatus(product, syncStatusTarget, lineItem)
        } else {
          throw new Error('Product not found in search results')
        }
      } else {
        throw new Error('Failed to search for product')
      }
    } catch (error) {
      console.error('Error refreshing product sync status:', error)
      syncStatusTarget.innerHTML = originalContent
      button.disabled = false
    }
  }
}
